import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Note } from '../../../../../../../entities/ApplicationFeed';
import { Application } from '../../../../../../../entities/Application';
import { AlertObject, AlertType } from '../../../../../../../components/Alert';
import {
  NoteService,
  NotesFeedResponse,
} from '../../../../../../../services/applicant_tracking/NoteService';
import { AddNoteModal } from './AddNoteModal';
import { NotesList } from './NotesList';
import { NotesSkeleton } from './NotesSkeleton';
import styles from '../../../styles.module.scss';

export const MAX_NOTES = 50;

const NOTES_PER_PAGE = 10;

interface PropTypes {
  application: Application;
  reloadKey: number;
  setAlert: (alert: AlertObject) => void;
  onNoteAdded: () => void;
}

export function NotesPanel(props: PropTypes) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentNotesPage, setCurrentNotesPage] =
    useState<NotesFeedResponse | null>(null);
  // Guards against out-of-order responses: when the user switches candidates
  // or triggers a reload mid-fetch, drop stale responses instead of letting
  // them overwrite the new view.
  const requestIdRef = useRef<number>(0);
  const scrollableId = `notes-panel-scroll-${props.application.id}`;

  const hasMore =
    currentNotesPage?.nextPage != null && notes.length < MAX_NOTES;

  function loadNotes(reload = false) {
    const page = reload ? 1 : currentNotesPage?.nextPage || 1;
    const requestId = ++requestIdRef.current;

    NoteService.list(
      props.application.id,
      page,
      NOTES_PER_PAGE,
      props.application.appliedAt,
    )
      .then((result) => {
        if (requestId !== requestIdRef.current) return;
        const filtered = result.data.filter((n) => !n.deletedAt);
        setNotes((prev) => (page === 1 ? filtered : prev.concat(filtered)));
        setCurrentNotesPage(result);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        props.setAlert({
          type: AlertType.Danger,
          message: 'There was an error loading notes.',
        });
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setIsLoading(false);
      });
  }

  useEffect(() => {
    setIsLoading(true);
    setNotes([]);
    setCurrentNotesPage(null);
    loadNotes(true);
  }, [props.application.id, props.reloadKey]);

  return (
    <div
      id={scrollableId}
      className={classNames(
        'border rounded-3 px-4 py-3 d-flex flex-column flex-shrink-0 me-3 my-3',
        styles['notes-panel'],
        styles['hidden-scrollbar'],
      )}
    >
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <span className='fw-bold text-dark-500 fs-5'>Notes</span>
        <AddNoteModal {...props} />
      </div>
      {isLoading ? (
        <NotesSkeleton rows={NOTES_PER_PAGE} />
      ) : notes.length === 0 ? (
        <span className='text-muted fs-5'>No notes yet.</span>
      ) : (
        <NotesList
          notes={notes}
          hasMore={hasMore}
          totalCount={currentNotesPage?.totalCount ?? 0}
          scrollableTarget={scrollableId}
          loadNotes={loadNotes}
        />
      )}
    </div>
  );
}
